import api from '../api/axios';

const getAllProposals = (skip = 0, take = 1000000, withDeleted, sort = 'createdAt,desc') => {
  const response = api.get(
    `/proposal?skip=${skip}&take=${take}&sort=${encodeURIComponent(sort)}&withDeleted=${withDeleted}`,
  );

  return response;
};

const getDeletedProposals = (skip = 0, take = 1000000) => {
  const response = api.get(`/proposal?skip=${skip}&take=${take}&sort=deletedAt%2Cdesc&sort=id%2Casc&onlyDeleted=true`);

  return response;
};

const getProposalById = (id) => {
  const response = api.get(`/proposal/${id}`);

  return response;
};

const getBatch = (ids) => {
  const idsArray = Array.isArray(ids) ? ids : ids.split(',');
  const response = api.post('/proposal/batch-load', { ids: idsArray });

  return response;
};

const create = (proposal) => {
  const response = api.post(`/proposal`, proposal);

  return response;
};

const update = (id, updatedProposal) => {
  const response = api.patch(`/proposal/${id}`, updatedProposal);

  return response;
};

const saveEmergencyScheduleRequest = (id, payload) => {
  return api.post(`/proposal/${id}/emergency-schedule-request`, payload);
};

const notifyEmergencyScheduleRequest = (id) => {
  return api.post(`/proposal/${id}/emergency-schedule-request/notify`);
};

const remove = (id) => {
  const response = api.delete(`/proposal/${id}`);

  return response;
};

const restore = (id) => {
  const response = api.get(`/proposal/${id}/restore`);

  return response;
};

const clone = (id) => {
  const response = api.post(`/proposal/${id}/clone`);

  return response;
};

const searchRequest = (skip = 0, take = 1000000, query, withDeleted = false, options = {}, sort = 'createdAt,desc') => {
  const response = api.get(`/proposal?skip=${skip}&take=${take}&sort=${encodeURIComponent(sort)}&query=${query}&withDeleted=${withDeleted}`, { ...options });

  return response;
};

const searchByFilters = (dateFrom, dateTo, status, user, skip = 0, take = 100) => {
  const response = api.get(
    `proposal?skip=${skip}&take=${take}&sort=createdAt%2Cdesc&withDeleted=false${
      dateFrom ? `&dateFrom=${dateFrom}` : ''
    }${dateTo ? `&dateTo=${dateTo}` : ''}${status === 'all' ? '' : `&status=${status}`}${
      user === 'all' ? '' : `&createdById=${user}`
    }`,
  );

  return response;
};

const searchByYearMonth = (year, month, status, user, proposalType, skip = 0, take = 100, query = '', sort = 'createdAt,desc') => {
  const response = api.get(
    `proposal?skip=${skip}&take=${take}&sort=${encodeURIComponent(sort)}&withDeleted=false${
      year ? `&year=${year}` : ''
    }${month ? `&month=${month}` : ''}${status === 'all' ? '' : `&status=${status}`}${
      user === 'all' ? '' : `&createdById=${user}`
    }${proposalType === 'all' ? '' : `&proposalType=${proposalType}`}${
      query ? `&query=${encodeURIComponent(query)}` : ''
    }`,
  );

  return response;
};

const sendEquipmentOrder = (id, content, replyTo) => {
  const response = api.post(`/equipmentOrder/${id}/sendEmail`, {
    content,
    replyTo,
  });

  return response;
};

const getEmailLogs = ({ page = 1, limit = 25, search = '' }) => {
  const response = api.get(`/proposal/emails?page=${page}&limit=${limit}&search=${search}`);
  return response;
};

const sendEmail = (id, email) => {
  const response = api.post(`/proposal/${id}/sendEmail`, email);

  return response;
};

// Sign proposal as authenticated user (technician/admin on behalf of customer)
const signProposal = (id, signature) => {
  const response = api.post(`/proposal/${id}/sign`, { signature });

  return response;
};

// System Templates
const getSystemTemplates = () => {
  const response = api.get('/proposalTemplate?skip=0&withDeleted=false');

  return response;
};

const getSystemTemplateById = (id) => {
  const response = api.get(`/proposalTemplate/${id}`);

  return response;
};

const createSystemTemplate = (data) => {
  const response = api.post('/proposalTemplate', data);

  return response;
};

const deleteSystemTemplate = (id) => {
  const response = api.delete(`/proposalTemplate/${id}`);

  return response;
};

const restoreSystemTemplate = (id) => {
  const response = api.get(`/proposalTemplate/${id}/restore`);

  return response;
};

// Payment reminder and receipt methods
const sendAmountDue = (id, data = {}) => {
  return api.post(`/proposal/${id}/send-amount-due`, data);
};

const sendPaymentReceipt = (id, data = {}) => {
  return api.post(`/proposal/${id}/send-payment-receipt`, data);
};

// Get PDF download URL - uses public endpoint if token provided, otherwise authenticated endpoint
const getReceiptPdfUrl = (id, token) => {
  if (token) {
    // Public access with client token
    return `${api.defaults.baseURL}/public/receipt/${id}?token=${token}`;
  } else {
    // Authenticated access - will use axios interceptor for auth header
    return `${api.defaults.baseURL}/proposal/${id}/receipt-pdf`;
  }
};

// Download PDF as blob for authenticated users
const downloadReceiptPdf = async (id) => {
  const response = await api.get(`/proposal/${id}/receipt-pdf`, {
    responseType: 'blob'
  });
  return response;
};

// Get permanent public link for proposal
const getPublicLink = (id) => {
  const response = api.get(`/proposal/${id}/public-link`);
  return response;
};

// Get client view history (counts + per-channel breakdown + recent opens)
const getViews = (id) => {
  const response = api.get(`/proposal/${id}/views`);
  return response;
};

// Foreman work instructions (LLM-generated crew checklist)
const generateForemanInstructions = (id) => {
  return api.post(`/proposal/${id}/foreman-instructions/generate`);
};

const saveForemanInstructions = (id, text) => {
  return api.put(`/proposal/${id}/foreman-instructions`, { text });
};

// ── Touches (Contact History) ──
const getTouches = (proposalId) => {
  return api.get(`/proposal/${proposalId}/touches/`);
};

const createTouch = (proposalId, data) => {
  return api.post(`/proposal/${proposalId}/touches/`, data);
};

const deleteTouch = (proposalId, touchId) => {
  return api.delete(`/proposal/${proposalId}/touches/${touchId}`);
};

const proposalsServices = {
  getAllProposals,
  getDeletedProposals,
  getProposalById,
  getBatch,
  create,
  update,
  saveEmergencyScheduleRequest,
  notifyEmergencyScheduleRequest,
  remove,
  restore,
  clone,
  searchRequest,
  searchByFilters,
  searchByYearMonth,
  sendEquipmentOrder,
  getEmailLogs,
  sendEmail,
  signProposal,
  getSystemTemplates,
  getSystemTemplateById,
  createSystemTemplate,
  deleteSystemTemplate,
  restoreSystemTemplate,
  sendAmountDue,
  sendPaymentReceipt,
  getReceiptPdfUrl,
  downloadReceiptPdf,
  getPublicLink,
  getViews,
  generateForemanInstructions,
  saveForemanInstructions,
  getTouches,
  createTouch,
  deleteTouch,
};

export default proposalsServices;
